<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useQuasar } from 'quasar'
import { useIntervalFn } from '@vueuse/core'
import { StaffDashboardPeriod } from '@roadfootage/api-client'
import { formatDate, formatNumber } from '@/utils/format'
import { storeToRefs } from 'pinia'
import { PERIOD_LABELS, useDashboardStore } from '../../stores/dashboard'
import { useModerationStore } from '../../stores/moderation'
import IncidentReviewDialog from '../../components/staff/IncidentReviewDialog.vue'
import MediaProcessingSection from '../../components/staff/MediaProcessingSection.vue'
import { formatIncidentType } from '@/utils/format'

const router = useRouter()
const $q = useQuasar()
const dashboardStore = useDashboardStore()
const moderationStore = useModerationStore()
const {
  metrics,
  mediaTiles,
  recentProblems,
  summaryFailed,
  period,
  recentUsers,
  recentIncidents,
  loading
} = storeToRefs(dashboardStore)

const periodOptions = [
  { label: '24h', value: StaffDashboardPeriod.Day },
  { label: '7d', value: StaffDashboardPeriod.Week },
  { label: '30d', value: StaffDashboardPeriod.Month }
]
const { queueTotal, queueItems, queueLoading } = storeToRefs(moderationStore)

const recentUserColumns = [
  {
    name: 'name',
    label: 'Name',
    field: (r: any) => r.name || r.displayName || 'Anonymous',
    align: 'left' as const
  },
  { name: 'email', label: 'Email', field: (r: any) => r.email || '—', align: 'left' as const },
  {
    name: 'createdAt',
    label: 'Created',
    field: 'createdAt',
    align: 'left' as const,
    format: (v: string) => (v ? formatDate(v) : '—')
  }
]

const recentIncidentColumns = [
  {
    name: 'type',
    label: 'Type',
    field: (r: any) => formatIncidentType(r.incidentReportType),
    align: 'left' as const
  },
  {
    name: 'status',
    label: 'Status',
    field: (r: any) => r.moderationStatus || '—',
    align: 'left' as const
  },
  {
    name: 'createdAt',
    label: 'Submitted',
    field: 'createdAt',
    align: 'left' as const,
    format: (v: string) => (v ? formatDate(v) : '—')
  }
]

const queueColumns = [
  {
    name: 'thumbnail',
    label: '',
    field: 'thumbnail',
    align: 'center' as const,
    style: 'width: 80px'
  },
  {
    name: 'submitted',
    label: 'Submitted',
    field: (row: any) => row.createdAt,
    align: 'left' as const,
    sortable: true
  },
  {
    name: 'type',
    label: 'Type',
    field: (row: any) => row.incidentReportType ?? '—',
    format: (val: string) => formatIncidentType(val),
    align: 'left' as const
  },
  {
    name: 'location',
    label: 'Location',
    field: (row: any) => row.address?.displayName || row.address?.rawInput || formatCoords(row),
    align: 'left' as const
  },
  {
    name: 'uploader',
    label: 'Uploader',
    field: (row: any) => row.userProfile?.name || 'Anonymous',
    align: 'left' as const
  },
  {
    name: 'files',
    label: 'Files',
    field: (row: any) => fileCount(row),
    align: 'center' as const
  },
  {
    name: 'actions',
    label: '',
    field: 'id',
    align: 'right' as const
  }
]

const formatCoords = (row: any) => {
  const coords = row.location?.coordinates
  if (!coords || coords.length < 2) return '—'
  return `${Number(coords[1]).toFixed(4)}, ${Number(coords[0]).toFixed(4)}`
}

const fileCount = (row: any) => {
  const videos = row.userIncidentFootageCollection?.length ?? 0
  const images = row.userIncidentImageCollection?.length ?? 0
  if (!videos && !images) return '—'
  const parts = []
  if (videos) parts.push(`${videos} video${videos !== 1 ? 's' : ''}`)
  if (images) parts.push(`${images} image${images !== 1 ? 's' : ''}`)
  return parts.join(', ')
}

const formatRelativeDate = (val: Date | string | undefined) => {
  if (!val) return '—'
  const d = typeof val === 'string' ? new Date(val) : val
  const diffMs = Date.now() - d.getTime()
  const diffHours = diffMs / (1000 * 60 * 60)
  if (diffHours < 1) return 'Just now'
  if (diffHours < 24) return `${Math.floor(diffHours)}h ago`
  const diffDays = diffHours / 24
  if (diffDays < 7) return `${Math.floor(diffDays)}d ago`
  return formatDate(d)
}

// First thumbnail URL from footage or images collection
const firstThumbnail = (row: any): string | null => {
  const footage = row.userIncidentFootageCollection ?? []
  const images = row.userIncidentImageCollection ?? []
  for (const f of [...footage, ...images]) {
    if (f.thumbnailUrl) return f.thumbnailUrl
  }
  return null
}

const isGridMode = computed(() => $q.screen.lt.md)

const navigateToFilteredList = (target?: string, query: Record<string, string> = {}) => {
  if (!target) return
  router.push({ name: target, query })
}

onMounted(() => {
  dashboardStore.fetchDashboardSummary()
  moderationStore.queueGrid.fetch()
})

useIntervalFn(() => {
  moderationStore.queueGrid.fetch()
  dashboardStore.fetchDashboardSummary()
}, 30_000)
</script>

<template>
  <q-page class="q-pa-md bg-grey-2">
    <!-- Metrics -->
    <div class="row items-center q-gutter-sm q-mb-md">
      <div class="text-caption text-grey-8">Period</div>
      <q-btn-toggle
        :model-value="period"
        :options="periodOptions"
        unelevated
        no-caps
        toggle-color="primary"
        color="white"
        text-color="grey-8"
        data-testid="period-toggle"
        @update:model-value="dashboardStore.setPeriod"
      />
    </div>
    <div class="row q-col-gutter-md q-mb-lg">
      <div v-for="metric in metrics" :key="metric.label" class="col-12 col-sm-6 col-md">
        <q-card
          flat
          bordered
          :class="{ 'cursor-pointer hover-shadow transition-03': metric.route }"
          :data-testid="metric.route ? 'metric-tile-link' : 'metric-tile'"
          @click="navigateToFilteredList(metric.route, metric.query)"
        >
          <q-card-section class="row items-center no-wrap">
            <div class="col">
              <div class="text-caption text-grey-7">{{ metric.label }}</div>
              <div
                class="text-h4 text-weight-bold"
                :class="metric.color || 'text-secondary'"
                data-testid="metric-count"
              >
                {{ metric.count === null ? '—' : formatNumber(metric.count) }}
              </div>
              <div v-if="metric.sub || !metric.route" class="text-caption text-grey-7">
                {{
                  [metric.sub, metric.route ? '' : 'No staff list yet'].filter(Boolean).join(' · ')
                }}
              </div>
            </div>
            <q-icon :name="metric.icon" size="md" color="grey-4" />
          </q-card-section>
        </q-card>
      </div>
    </div>

    <MediaProcessingSection
      :tiles="mediaTiles"
      :problems="recentProblems"
      :failed="summaryFailed"
      :period-label="PERIOD_LABELS[period]"
      @navigate="(t) => navigateToFilteredList(t.route, t.query)"
    />

    <!-- Recent data tables -->
    <div class="row q-col-gutter-md q-mb-lg">
      <div class="col-12 col-md-6">
        <q-table
          flat
          bordered
          title="Recent Users"
          :rows="recentUsers"
          :columns="recentUserColumns"
          :loading="loading"
          row-key="id"
          hide-pagination
          class="bg-white"
        />
      </div>
      <div class="col-12 col-md-6">
        <q-table
          flat
          bordered
          title="Recent Incidents"
          :rows="recentIncidents"
          :columns="recentIncidentColumns"
          :loading="loading"
          row-key="id"
          hide-pagination
          class="bg-white"
        />
      </div>
    </div>

    <!-- Moderation Queue -->
    <q-card flat bordered class="bg-white">
      <q-table
        flat
        :title="`Moderation Queue${queueTotal > 0 ? ` (${queueTotal})` : ''}`"
        :rows="queueItems"
        :columns="queueColumns"
        :loading="queueLoading"
        :grid="isGridMode"
        row-key="id"
        :rows-per-page-options="[10, 25, 50, 0]"
      >
        <!-- Desktop row template -->
        <template #body="props">
          <q-tr :props="props">
            <q-td key="thumbnail" :props="props" auto-width>
              <q-img
                v-if="firstThumbnail(props.row)"
                :src="firstThumbnail(props.row)!"
                width="72px"
                ratio="1.77"
                class="rounded-borders"
              />
              <q-icon v-else name="mdi-image-off" size="sm" color="grey-5" />
            </q-td>
            <q-td key="submitted" :props="props">
              {{ formatRelativeDate(props.row.createdAt) }}
            </q-td>
            <q-td key="type" :props="props">
              {{ props.row.incidentReportType ?? '—' }}
            </q-td>
            <q-td key="location" :props="props" class="ellipsis" style="max-width: 200px">
              <span
                :title="
                  props.row.address?.displayName ||
                  props.row.address?.rawInput ||
                  formatCoords(props.row)
                "
              >
                {{
                  props.row.address?.displayName ||
                  props.row.address?.rawInput ||
                  formatCoords(props.row)
                }}
              </span>
            </q-td>
            <q-td key="uploader" :props="props">
              {{ props.row.userProfile?.name || 'Anonymous' }}
            </q-td>
            <q-td key="files" :props="props" class="text-center">
              {{ fileCount(props.row) }}
            </q-td>
            <q-td key="actions" :props="props" auto-width>
              <q-btn
                unelevated
                dense
                color="primary"
                size="sm"
                icon="mdi-eye"
                aria-label="Review incident"
                @click="moderationStore.openIncident(props.row.id)"
              />
            </q-td>
          </q-tr>
        </template>

        <!-- Mobile grid card template -->
        <template #item="props">
          <div class="col-12 col-sm-6 q-pa-xs">
            <q-card flat bordered>
              <div class="row no-wrap items-start q-pa-sm q-gutter-sm">
                <!-- Thumbnail -->
                <q-img
                  v-if="firstThumbnail(props.row)"
                  :src="firstThumbnail(props.row)!"
                  width="80px"
                  ratio="1.77"
                  class="rounded-borders flex-shrink-0"
                />
                <div
                  v-else
                  class="flex flex-center bg-grey-3 rounded-borders flex-shrink-0"
                  style="width: 80px; aspect-ratio: 16/9"
                >
                  <q-icon name="mdi-image-off" size="sm" color="grey-5" />
                </div>

                <!-- Details -->
                <div class="col q-gutter-y-xs text-caption">
                  <div>
                    <span class="text-grey-7">Submitted: </span>
                    <strong>{{ formatRelativeDate(props.row.createdAt) }}</strong>
                  </div>
                  <div>
                    <span class="text-grey-7">Type: </span>
                    {{ props.row.incidentReportType ?? '—' }}
                  </div>
                  <div class="ellipsis">
                    <span class="text-grey-7">Location: </span>
                    {{
                      props.row.address?.displayName ||
                      props.row.address?.rawInput ||
                      formatCoords(props.row)
                    }}
                  </div>
                  <div>
                    <span class="text-grey-7">Uploader: </span>
                    {{ props.row.userProfile?.name || 'Anonymous' }}
                  </div>
                  <div>
                    <span class="text-grey-7">Files: </span>
                    {{ fileCount(props.row) }}
                  </div>
                </div>
              </div>
              <q-separator />
              <q-card-actions class="q-pa-sm">
                <q-btn
                  unelevated
                  color="primary"
                  icon="mdi-eye"
                  aria-label="Review incident"
                  class="full-width"
                  @click="moderationStore.openIncident(props.row.id)"
                />
              </q-card-actions>
            </q-card>
          </div>
        </template>

        <template #no-data>
          <div class="full-width row flex-center q-pa-xl text-grey-6">
            <div class="text-center">
              <q-icon name="mdi-party-popper" size="xl" color="primary" />
              <div class="text-h6 q-mt-md">No incidents requiring action. Enjoy the quiet!</div>
            </div>
          </div>
        </template>
      </q-table>
    </q-card>

    <IncidentReviewDialog />
  </q-page>
</template>

<style lang="scss" scoped>
.hover-shadow:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05) !important;
}
.transition-03 {
  transition: all 0.3s ease;
}
</style>
